import i18n from '@/i18n';

interface ApiErrorBody {
  error: string;
  errorCode?: string;
  errors?: Record<string, string[]>;
}

/**
 * Extracts a user-facing error message from an RTK Query error or caught exception.
 * Lookup chain: i18n translation by errorCode → generic fallback.
 */
export function getErrorMessage(error: unknown): string {
  const body = extractErrorBody(error);
  if (!body) return i18n.t('common.error');

  if (body.errorCode) {
    const key = `errors.${body.errorCode}`;
    if (i18n.exists(key)) return i18n.t(key);
  }

  return i18n.t('common.error');
}

/**
 * Extracts the error body from an RTK Query FetchBaseQueryError.
 * RTK Query surfaces the JSON response body in `error.data`.
 */
export function extractErrorBody(error: unknown): ApiErrorBody | null {
  if (
    error &&
    typeof error === 'object' &&
    'data' in error &&
    error.data &&
    typeof error.data === 'object'
  ) {
    return error.data as ApiErrorBody;
  }
  return null;
}

/**
 * Type guard: returns true if an RTK Query mutation result is an error.
 */
export function isApiError(result: { error: unknown } | { data: unknown }): result is { error: unknown } {
  return 'error' in result;
}
